'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, Users, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { driverQueries } from '@/features/drivers';
import type { Driver } from '@/features/drivers';
import { DriverCard } from './driver-card';

interface DriverSelectionPanelProps {
  selectedDriverId: number | null;
  onSelectDriver: (driver: Driver) => void;
}

export function DriverSelectionPanel({
  selectedDriverId,
  onSelectDriver
}: DriverSelectionPanelProps) {
  const t = useTranslations('shipping');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const {
    items: drivers,
    isLoading,
    isFetchingNextPage,
    loadMoreRef
  } = useInfiniteScroll<Driver>(
    driverQueries.infiniteList({
      status: ['active'],
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    })
  );

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        <div className='flex items-center gap-2'>
          <Users className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>{t('availableDrivers')}</CardTitle>
        </div>
        <div className='relative'>
          <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          <Input
            placeholder={t('searchDrivers')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='ps-9'
          />
        </div>
      </CardHeader>
      <CardContent
        className='flex-1 overflow-y-auto pb-4'
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        {isLoading ? (
          <div className='space-y-3'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full rounded-lg' />
            ))}
          </div>
        ) : drivers.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <Users className='text-muted-foreground mb-3 h-10 w-10' />
            <p className='text-muted-foreground text-sm'>
              {t('noDriversFound')}
            </p>
          </div>
        ) : (
          <div className={cn('space-y-3')}>
            {drivers.map((driver) => (
              <DriverCard
                key={driver.id}
                driver={driver}
                selected={selectedDriverId === driver.id}
                onSelect={() => onSelectDriver(driver)}
              />
            ))}
          </div>
        )}
        {isFetchingNextPage && (
          <div className='flex justify-center py-4'>
            <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
          </div>
        )}
        <div ref={loadMoreRef} className='h-px' />
      </CardContent>
    </Card>
  );
}
